import { Box, Divider, Stack } from '@mui/material';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SubmitHandler } from 'react-hook-form';
import EditSportBackgroundForm, {
  EditFormRef,
} from './EditSportBackgroundForm';
import { transformSportBackgroundForEdit } from './transform-sport-background-data';
import CustomButton from '../../../../components/MUIComponents/CustomButton';
import SnackbarCustom from '../../../../components/MUIComponents/SnackbarCustom';
import { ButtonTypeEnum } from '../../../../enums/button-type-enum';
import { SnackbarSeverityEnum } from '../../../../enums/snackbar-severity-enum';
import {
  ISportBackgroundFormData,
  IUserSportBackgroundResponse,
} from '../../../../interfaces/user-sport-background.interface';
import { useUpsertUserSportBackgroundMutation } from '../../../../store/apiSlice/sportBackgroundApi';
import { errorHelper } from '../../../../utils/helper/error-helper';
import { createTeamSchema } from '../../../../validation/sport-validation';

interface EditSportBackgroundProps {
  sportBg: IUserSportBackgroundResponse;
  openSportBackgroundEditClose: (sportBgId: string) => void;
}

const EditSportBackground = (
  { sportBg, openSportBackgroundEditClose }: EditSportBackgroundProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarSeverityEnum>(SnackbarSeverityEnum.ERROR);

  const [upsertUserSportBg, { isLoading: isSaving }] =
    useUpsertUserSportBackgroundMutation();

  // New form ref
  const formRef = useRef<EditFormRef>(null);

  // Transform saved data for new form
  const savedSportBackgrounds = useMemo(
    () => transformSportBackgroundForEdit(sportBg),
    [sportBg]
  );

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const onSubmitSport: SubmitHandler<ISportBackgroundFormData> = useCallback(
    async (data) => {
      const toIsoDate = (value: any, label: string) => {
        if (!value) {
          throw new Error(`${label} must be a valid date.`);
        }

        if (typeof value === 'object' && value !== null) {
          const maybeDayjs = value as {
            isValid?: () => boolean;
            toDate?: () => Date;
          };

          if (
            typeof maybeDayjs.isValid === 'function' &&
            typeof maybeDayjs.toDate === 'function'
          ) {
            if (!maybeDayjs.isValid()) {
              throw new Error(`${label} must be a valid date.`);
            }
            const d = maybeDayjs.toDate();
            if (Number.isNaN(d.getTime())) {
              throw new Error(`${label} must be a valid date.`);
            }
            return d.toISOString();
          }
        }

        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) {
          throw new Error(`${label} must be a valid date.`);
        }
        return d.toISOString();
      };

      // Validate teams form
      try {
        for (const sportBackground of data.sportBackgrounds) {
          for (const team of sportBackground.teams || []) {
            try {
              await createTeamSchema(
                (sportBackground.isOtherSport || team.isIndividualSport) ??
                  false
              ).validate(team, {
                abortEarly: false,
              });
              // If valid, proceed with submission
            } catch (validationErrors: any) {
              const errorMessages: any[] = validationErrors.inner.map(
                (error: any) => ({
                  path: error.path, // The field with the error
                  message: error.message, // The error message
                })
              );

              const errorToString = errorMessages.reduce<string>(
                (acc, cur) => acc + `${cur.message}\n`,
                ''
              );

              throw new Error(errorToString); // Throw the error to propagate it to the outer catch block
            }
          }
        }
        // Continue with the next code if all validations pass
      } catch (error) {
        // Handle the error from any team validation
        setSnackbarMessage(errorHelper(error));
        setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
        setSnackbarOpen(true);

        return;
      }

      const sportIds = [] as string[];
      const uniqueSportIds = new Set();

      data.sportBackgrounds.forEach((sportBackground) => {
        if (!uniqueSportIds.has(sportBackground.sportId)) {
          uniqueSportIds.add(sportBackground.sportId);
          if (sportBackground.sportId.length > 0) {
            sportIds.push(sportBackground.sportId);
          }
        }
      });

      if (sportIds.length === 0) {
        setSnackbarMessage('Please select at least one sport.');
        setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
        setSnackbarOpen(true);
        return;
      }

      try {
        await Promise.all(
          data.sportBackgrounds.map(async (sportBackground) => {
            const formData = new FormData();

            if (sportBackground.id) {
              formData.append('id', sportBackground.id);
            }
            formData.append('sportId', sportBackground.sportId);
            if (sportBackground.description) {
              formData.append('description', sportBackground.description);
            }
            if (sportBackground.startCareerDate instanceof Date) {
              formData.append(
                'startCareerDate',
                sportBackground.startCareerDate.toISOString()
              );
            }
            if (
              sportBackground.endCareerDate &&
              sportBackground.endCareerDate instanceof Date
            ) {
              formData.append(
                'endCareerDate',
                sportBackground.endCareerDate.toISOString()
              );
            }
            formData.append(
              'activeCareer',
              String(sportBackground.isActiveSport)
            );

            let newFileIndex = 0;
            const attachmentIdsToKeep: string[] = [];

            if (
              sportBackground.fileData &&
              sportBackground.fileData.length > 0
            ) {
              sportBackground.fileData.forEach((fileData) => {
                const attachmentId = (fileData as any).attachmentId;

                if (attachmentId) {
                  attachmentIdsToKeep.push(attachmentId);
                  if (fileData.description) {
                    formData.append(
                      `existingAttachments[${attachmentId}].description`,
                      fileData.description
                    );
                  }
                  if (fileData.links && fileData.links.length > 0) {
                    fileData.links.forEach((link, linkIndex) => {
                      formData.append(
                        `existingAttachments[${attachmentId}].links[${linkIndex}]`,
                        link
                      );
                    });
                  }
                } else if (fileData.file) {
                  formData.append('documents', fileData.file);
                  if (fileData.description) {
                    formData.append(
                      `documents[${newFileIndex}].description`,
                      fileData.description
                    );
                  }
                  if (fileData.links && fileData.links.length > 0) {
                    fileData.links.forEach((link, linkIndex) => {
                      formData.append(
                        `documents[${newFileIndex}].links[${linkIndex}]`,
                        link
                      );
                    });
                  }
                  newFileIndex++;
                }
              });
            }

            formData.append(
              'attachmentIdsToKeep',
              JSON.stringify(attachmentIdsToKeep)
            );

            const teamsData = (sportBackground.teams || []).map((team) => ({
              id: team.id,
              leagueId: team.leagueId || '',
              leagueName: team.leagueName || '',
              teamId: team.teamId || '',
              teamName: team.teamName || '',
              startDate: toIsoDate(team.startDateInTeam, 'Start date'),
              endDate: team.isActiveTeam
                ? null
                : toIsoDate(team.endDateInTeam, 'End date'),
              activeCareer: !!team.isActiveTeam,
            }));
            formData.append('teams', JSON.stringify(teamsData));

            const teamIdsToKeep = teamsData
              .filter((t) => t.id)
              .map((t) => t.id);
            formData.append('teamIdsToKeep', JSON.stringify(teamIdsToKeep));

            await upsertUserSportBg(formData).unwrap();
          })
        );
        setSnackbarMessage('Sport background successfully saved.');
        setSnackbarSeverity(SnackbarSeverityEnum.SUCCESS);
        setSnackbarOpen(true);
      } catch (error: any) {
        const errorMessage = errorHelper(error);
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
        setSnackbarOpen(true);
      }
      setTimeout(() => {
        openSportBackgroundEditClose(sportBg.id);
      }, 1000);
    },
    [sportBg.id, upsertUserSportBg, openSportBackgroundEditClose]
  );

  return (
    <Box ref={ref} sx={{ scrollMarginTop: 72 }}>
      <EditSportBackgroundForm
        ref={formRef}
        savedSportBackgrounds={savedSportBackgrounds}
      />

      <Divider sx={{ m: '48px 0 35px' }} />

      <Stack direction="row" columnGap="16px">
        <CustomButton
          onClick={() => {
            if (formRef.current) {
              formRef.current.handleSubmit(onSubmitSport, () => {
                // On validation error, scroll to first error
                formRef.current?.scrollToError();
              })();
            }
          }}
          variant="outlined"
          buttonWidth={220}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'save'}
        </CustomButton>
        <CustomButton
          onClick={() => openSportBackgroundEditClose(sportBg.id)}
          variant="outlined"
          buttonWidth={220}
          variantType={ButtonTypeEnum.SECONDARY}
          disabled={isSaving}
        >
          cancel
        </CustomButton>
      </Stack>

      <SnackbarCustom
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default forwardRef(EditSportBackground);
